import React, { useEffect, useRef } from 'react';
import pluralize from 'pluralize';
import { useMutation } from '@tanstack/react-query';

import { APIbulkUpdateSprkImages } from '../../../api/Assets';

import type { Asset } from '../../../types/asset';

interface AssetDeleteModalProps {
  assets: Asset[];
  show: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
};

const AssetDeleteModal: React.FC<AssetDeleteModalProps> = ({ assets, show, onConfirm, onDismiss }) => {
  const dialogRef = useRef<HTMLElement>(null);
  const isSingle = assets.length === 1;
  const imagesInSprks = assets.filter(asset => asset.memories_count > 0);
  const sprksCount = imagesInSprks.reduce((total, asset) => total + asset.memories_count, 0);

  const { mutate: deleteAssets, isPending: deleting, error } = useMutation({
    mutationFn: () => APIbulkUpdateSprkImages({ action: 'delete', ids: assets.map(asset => asset.id) }),
    onSuccess: onConfirm,
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    const handleHide = (event: Event) => {
      if (event.target !== dialog) {
        return;
      }
      if (deleting) {
        event.preventDefault();
      } else {
        onDismiss();
      }
    };
    dialog?.addEventListener('wa-hide', handleHide);
    return () => dialog?.removeEventListener('wa-hide', handleHide);
  }, [deleting, onDismiss]);

  const imageName = (asset: Asset) => asset.title || asset.original_filename;

  return (
    <wa-dialog
      ref={(dialog) => { dialogRef.current = dialog; }}
      label={isSingle ? 'Delete image' : 'Delete images'}
      id="dialog-delete-asset"
      className="dialog-delete-user"
      open={show}
    >
      <div className="delete-user-header">
        <strong>
          {isSingle
            ? `Are you sure you want to delete the image "${imageName(assets[0])}"?`
            : `Are you sure you want to delete ${pluralize('image', assets.length, true)}?`}
        </strong><br />
        This action cannot be undone.
      </div>
      <div className="delete-user-content wa-stack wa-gap-2">
        {sprksCount > 0 && (
          <wa-callout variant="danger" className="delete-user-warning">
            <i className="fa-regular fa-circle-exclamation" slot="icon"></i>
            <strong>
              {isSingle
                ? `This image is used in ${pluralize('SPRK', sprksCount, true)}.`
                : `${pluralize('image', imagesInSprks.length, true)} ${imagesInSprks.length === 1 ? 'is' : 'are'} used in ${pluralize('SPRK', sprksCount, true)}.`}
            </strong><br />
            {imagesInSprks.length === 1 ? 'It' : 'They'} will be removed from {sprksCount === 1 ? 'that SPRK' : 'those SPRKs'}.
          </wa-callout>
        )}
        {error && <div className="error-message" role="alert">{error.message || 'Failed to delete. Please try again.'}</div>}
      </div>
      <wa-button slot="footer" size="s" appearance="outlined" disabled={deleting} onClick={onDismiss}>
        Close
      </wa-button>
      <wa-button slot="footer" size="s" variant="brand" disabled={deleting} onClick={() => deleteAssets()}>
        {deleting ? 'Deleting...' : isSingle ? 'Delete image' : `Delete ${pluralize('image', assets.length, true)}`}
      </wa-button>
    </wa-dialog>
  );
}

export default AssetDeleteModal;
